import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Plus, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface DiffRow {
  status: "matched" | "missing_in_estimate" | "missing_in_claim" | "price_delta";
  description: string;
  claim?: { quantity: number | null; unit: string | null; unit_price: number | null; rcv: number | null };
  estimate?: { quantity: number | null; unit: string | null; unit_price: number | null; total: number | null };
  delta?: number | null;
}

interface Props {
  claimEstimateId: string;
  jobId: string;
  orgId: string;
  onClose: () => void;
}

const fmt = (n: number | null | undefined) =>
  n == null ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export const ClaimVsEstimateView = ({ claimEstimateId, jobId, orgId, onClose }: Props) => {
  const [rows, setRows] = useState<DiffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data, error } = await supabase.functions.invoke("compare-claim-vs-estimate", {
          body: { claim_estimate_id: claimEstimateId, job_id: jobId },
        });
        if (error) throw error;
        setRows(data?.rows || []);
      } catch (e: any) {
        toast.error(e?.message || "Compare failed");
      } finally {
        setLoading(false);
      }
    })();
  }, [claimEstimateId, jobId]);

  const addToSupplement = async (row: DiffRow) => {
    if (!row.claim) return;
    setAdding(row.description);
    try {
      const qty = row.claim.quantity ?? 1;
      const unitPrice = row.claim.unit_price ?? 0;
      const { error } = await supabase.from("supplement_line_items").insert({
        job_id: jobId,
        org_id: orgId,
        item_name: row.description.slice(0, 80),
        description: row.description,
        quantity: qty,
        unit_type: row.claim.unit,
        unit_price: unitPrice,
        total_cost: row.claim.rcv ?? qty * unitPrice,
        is_ai_suggested: false,
        category: "claim_import",
        line_order: 0,
      });
      if (error) throw error;
      toast.success("Added to supplement");
    } catch (e: any) {
      toast.error(e?.message || "Add failed");
    } finally {
      setAdding(null);
    }
  };

  const statusBadge = (s: DiffRow["status"]) => {
    const map: Record<DiffRow["status"], { label: string; variant: any }> = {
      matched: { label: "Matched", variant: "secondary" },
      missing_in_estimate: { label: "Missing in our estimate", variant: "destructive" },
      missing_in_claim: { label: "Not in claim", variant: "outline" },
      price_delta: { label: "Price delta", variant: "default" },
    };
    return <Badge variant={map[s].variant}>{map[s].label}</Badge>;
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Claim vs Internal Estimate</CardTitle>
        <Button variant="ghost" size="sm" onClick={onClose}><X className="h-4 w-4" /></Button>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Comparing…
          </div>
        ) : rows.length === 0 ? (
          <div className="py-6 text-center text-sm text-muted-foreground">No comparable items found.</div>
        ) : (
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-12 gap-2 rounded-md border p-3 text-sm">
                <div className="col-span-5">
                  <div className="font-medium">{r.description}</div>
                  <div className="mt-1">{statusBadge(r.status)}</div>
                </div>
                <div className="col-span-3">
                  <div className="text-xs text-muted-foreground">Claim</div>
                  {r.claim ? (
                    <div className="tabular-nums">
                      {r.claim.quantity ?? "—"} {r.claim.unit || ""} @ {fmt(r.claim.unit_price)} = {fmt(r.claim.rcv)}
                    </div>
                  ) : <div className="text-muted-foreground">—</div>}
                </div>
                <div className="col-span-3">
                  <div className="text-xs text-muted-foreground">Our estimate</div>
                  {r.estimate ? (
                    <div className="tabular-nums">
                      {r.estimate.quantity ?? "—"} {r.estimate.unit || ""} @ {fmt(r.estimate.unit_price)} = {fmt(r.estimate.total)}
                    </div>
                  ) : <div className="text-muted-foreground">—</div>}
                </div>
                <div className="col-span-1 flex items-start justify-end">
                  {(r.status === "missing_in_estimate" || r.status === "price_delta") && r.claim && (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={adding === r.description}
                      onClick={() => addToSupplement(r)}
                    >
                      {adding === r.description ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus className="h-3 w-3" />}
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
